import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Download, Flag, FileWarning, Loader2, Mail, PenLine, RefreshCw, UserCheck, UserX, XCircle
} from 'lucide-react';
import { PersonalSummary, LicenseSummary, RecordSummary, EmploymentSummary, SummaryCard, Rows } from '../components/application/ApplicationSummary';
import { APPLICATION_STATUS_LABELS, STATUS_BADGE_CLASSES, STEP_LABELS, POSITION_OPTIONS } from '../components/application/stepConfig';
import DocumentGallery from '../components/drivers/applications/DocumentGallery';
import ReviewModal from '../components/drivers/applications/ReviewModal';
import ApplicationTimeline from '../components/drivers/applications/ApplicationTimeline';
import { RequestChangesModal, RejectModal, ApproveSignModal, HireModal } from '../components/drivers/applications/ReviewActionModals';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/ToastProvider';
import driverApplicationService, { errorMessage } from '../services/driverApplicationService';
import { formatDisplayDateTime, formatPhoneInput } from '../utils/dateInput';

const NAV = [
  { id: 'personal', label: STEP_LABELS.personal },
  { id: 'license', label: STEP_LABELS.license },
  { id: 'record', label: STEP_LABELS.record },
  { id: 'employment', label: STEP_LABELS.employment },
  { id: 'documents', label: 'Documents' },
  { id: 'signature', label: 'Disclosures & signature' },
  { id: 'timeline', label: 'Activity' }
];

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASSES[status] || 'border-gray-200 bg-gray-100 text-gray-700'}`}>
    {APPLICATION_STATUS_LABELS[status] || status}
  </span>
);

const ActionButton = ({ icon: Icon, tone = 'neutral', children, ...rest }) => {
  const tones = {
    neutral: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
    warning: 'border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100',
    danger: 'border border-red-200 bg-white text-red-700 hover:bg-red-50',
    success: 'bg-green-700 text-white hover:bg-green-800 shadow-sm',
    primary: 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
  };
  return (
    <button type="button" className={`inline-flex items-center justify-center gap-1.5 rounded-md px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60 sm:py-2 ${tones[tone]}`} {...rest}>
      {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
      {children}
    </button>
  );
};

/**
 * Manager review of one driver application: read-only sections with
 * per-section "flag for changes", documents, signature record, activity, and
 * the status-dependent actions (request changes, reject, approve & sign, hire).
 */
const ApplicationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { userData } = useAuth();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flagged, setFlagged] = useState([]);
  const [modal, setModal] = useState(null);
  const [busyAction, setBusyAction] = useState('');

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const data = await driverApplicationService.get(id);
      setBundle(data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load this application.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const application = bundle?.application;
  const status = application?.status;
  const latestRequest = useMemo(() => {
    const requests = application?.review?.changeRequests || [];
    return requests.length ? requests[requests.length - 1] : null;
  }, [application]);

  const toggleFlag = (step) => setFlagged((prev) => (prev.includes(step) ? prev.filter((s) => s !== step) : [...prev, step]));

  const afterAction = (updated, message, driver) => {
    setModal(null);
    setFlagged([]);
    toast.success(message);
    if (driver?.id) {
      navigate(`/drivers?driver=${encodeURIComponent(driver.id)}`);
      return;
    }
    setBundle((prev) => (prev ? { ...prev, application: updated } : prev));
    load({ quiet: true });
  };

  const openPdf = async (version) => {
    setBusyAction(`pdf-${version}`);
    try {
      const { url } = await driverApplicationService.getPdfUrl(application.id, version);
      // Attachment URL: navigating downloads the file and keeps this page.
      window.location.assign(url);
    } catch (err) {
      toast.error(errorMessage(err, 'The PDF is not ready yet.'));
    } finally {
      setBusyAction('');
    }
  };

  const regenerate = async () => {
    const version = String(application.pdfError || '').startsWith('final') ? 'final' : 'submitted';
    setBusyAction('regenerate');
    try {
      await driverApplicationService.regeneratePdf(application.id, version);
      toast.success('PDF regenerated');
      await load({ quiet: true });
    } catch (err) {
      toast.error(errorMessage(err, 'Could not regenerate the PDF.'));
    } finally {
      setBusyAction('');
    }
  };

  const inviteAction = async (action) => {
    setBusyAction(action);
    try {
      if (action === 'resend') {
        const result = await driverApplicationService.resendInvite(application.id);
        toast.success(result.emailSent ? `Invite re-sent to ${application.applicant.email}` : 'New invite link generated. Email is not configured, so share the link manually.');
      } else {
        await driverApplicationService.revokeInvite(application.id);
        setModal(null);
        toast.success('Invite withdrawn');
      }
      await load({ quiet: true });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyAction('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" /> Loading application…
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center">
        <FileWarning className="mx-auto h-10 w-10 text-gray-400" aria-hidden="true" />
        <p className="mt-3 text-sm text-gray-700">{error || 'Application not found.'}</p>
        <div className="mt-5 flex justify-center gap-2">
          <Link to="/drivers?tab=applications" className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Back to applications</Link>
          <button type="button" onClick={() => load()} className="btn-primary">Try again</button>
        </div>
      </div>
    );
  }

  const personal = application.sections?.personal || {};
  const name = application.applicantName || `${application.applicant?.firstName || ''} ${application.applicant?.lastName || ''}`.trim();
  const canFlag = status === 'submitted';
  const hasSubmittedPdf = Boolean(application.submittedPdf);
  const hasFinalPdf = Boolean(application.review?.finalPdf);
  const sig = application.applicantSignature;
  const rsig = application.review?.reviewerSignature;
  const disclosures = application.sections?.disclosures;
  const legalTitles = Object.fromEntries((bundle.legal?.disclosures || []).map((d) => [d.id, d.shortTitle || d.title]));

  const flagAction = (step) => (canFlag ? (
    <button
      type="button"
      onClick={() => toggleFlag(step)}
      aria-pressed={flagged.includes(step)}
      className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-amber-400 ${flagged.includes(step) ? 'border-amber-400 bg-amber-100 text-amber-900' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}`}
    >
      <Flag className="h-3.5 w-3.5" aria-hidden="true" /> {flagged.includes(step) ? 'Flagged' : 'Flag for changes'}
    </button>
  ) : null);
  const cardProps = (step) => ({
    headerAction: flagAction(step),
    highlighted: flagged.includes(step) || (status === 'changes_requested' && (latestRequest?.sections || []).includes(step))
  });

  const actions = [];
  if (status === 'invited' || status === 'expired') {
    actions.push(<ActionButton key="resend" icon={Mail} disabled={busyAction === 'resend'} onClick={() => inviteAction('resend')}>{status === 'expired' ? 'Send new invite' : 'Resend invite'}</ActionButton>);
  }
  if (['invited', 'in_progress', 'expired'].includes(status)) {
    actions.push(<ActionButton key="withdraw" icon={UserX} tone="danger" onClick={() => setModal('withdraw')}>Withdraw invite</ActionButton>);
  }
  if (['submitted', 'changes_requested', 'approved'].includes(status)) {
    actions.push(<ActionButton key="reject" icon={XCircle} tone="danger" onClick={() => setModal('reject')}>Not moving forward</ActionButton>);
  }
  if (status === 'submitted') {
    actions.push(<ActionButton key="changes" icon={PenLine} tone="warning" onClick={() => setModal('changes')}>Request changes{flagged.length ? ` (${flagged.length})` : ''}</ActionButton>);
    actions.push(<ActionButton key="approve" icon={CheckCircle2} tone="success" onClick={() => setModal('approve')}>Approve & sign</ActionButton>);
  }
  if (status === 'approved') {
    actions.push(<ActionButton key="hire" icon={UserCheck} tone="success" onClick={() => setModal('hire')}>Hire driver</ActionButton>);
  }
  if (status === 'hired' && application.hire?.driverDocId) {
    actions.push(<ActionButton key="profile" icon={UserCheck} tone="primary" onClick={() => navigate(`/drivers?driver=${encodeURIComponent(application.hire.driverDocId)}`)}>View driver profile</ActionButton>);
  }

  return (
    <div className="pb-28">
      <Link to="/drivers?tab=applications" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Applications
      </Link>

      <header className="mt-3 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-2xl font-bold text-gray-900">{name || application.applicant?.email}</h1>
              <StatusBadge status={status} />
            </div>
            <p className="mt-1 text-sm text-gray-600">
              {application.applicant?.email}
              {personal.phone ? ` · ${formatPhoneInput(personal.phone)}` : ''}
              {` · ${POSITION_OPTIONS.find((o) => o.value === application.position?.type)?.label || 'Driver'}`}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {application.submittedAt ? `Submitted ${formatDisplayDateTime(application.submittedAt)}` : `Invited ${formatDisplayDateTime(application.invite?.sentAt)} · ${application.progress?.percent ?? 0}% complete`}
              {application.review?.decidedAt ? ` · ${status === 'rejected' ? 'Closed' : 'Approved'} ${formatDisplayDateTime(application.review.decidedAt)} by ${application.review.decidedBy?.name || 'a reviewer'}` : ''}
            </p>
          </div>
          <div className="flex flex-shrink-0 flex-wrap gap-2">
            {hasSubmittedPdf && (
              <ActionButton icon={busyAction === 'pdf-submitted' ? Loader2 : Download} disabled={busyAction === 'pdf-submitted'} onClick={() => openPdf('submitted')}>Submitted PDF</ActionButton>
            )}
            {hasFinalPdf && (
              <ActionButton icon={busyAction === 'pdf-final' ? Loader2 : Download} disabled={busyAction === 'pdf-final'} onClick={() => openPdf('final')}>Signed PDF</ActionButton>
            )}
          </div>
        </div>

        {application.pdfError && (
          <div className="mt-4 flex flex-col gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 sm:flex-row sm:items-center sm:justify-between">
            <span className="inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" aria-hidden="true" /> The PDF could not be generated ({application.pdfError}).</span>
            <ActionButton icon={RefreshCw} disabled={busyAction === 'regenerate'} onClick={regenerate}>{busyAction === 'regenerate' ? 'Regenerating…' : 'Regenerate PDF'}</ActionButton>
          </div>
        )}
        {status === 'changes_requested' && latestRequest && (
          <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-semibold">Waiting for the applicant to update: {latestRequest.sections.map((s) => STEP_LABELS[s] || s).join(', ')}</p>
            <p className="mt-1 whitespace-pre-line">{latestRequest.message}</p>
            <p className="mt-1 text-xs text-amber-800">Requested {formatDisplayDateTime(latestRequest.requestedAt)} by {latestRequest.requestedBy?.name}</p>
          </div>
        )}
        {status === 'rejected' && application.review?.reason && (
          <p className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700"><span className="font-medium">Internal reason:</span> {application.review.reason}</p>
        )}
      </header>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[200px_minmax(0,1fr)]">
        <nav aria-label="Application sections" className="hidden lg:block">
          <ul className="sticky top-4 space-y-1 text-sm">
            {NAV.map((item) => (
              <li key={item.id}>
                <a href={`#section-${item.id}`} className="flex items-center justify-between rounded-md px-3 py-2 text-gray-700 hover:bg-white hover:text-gray-900">
                  {item.label}
                  {flagged.includes(item.id) && <Flag className="h-3.5 w-3.5 text-amber-600" aria-label="flagged" />}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0 space-y-5">
          {!application.submittedAt && (
            <p className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
              This application has not been submitted yet. You are seeing what the applicant has saved so far.
            </p>
          )}
          <PersonalSummary application={application} cardProps={cardProps('personal')} />
          <LicenseSummary application={application} showDocuments={false} cardProps={cardProps('license')} />
          <RecordSummary application={application} cardProps={cardProps('record')} />
          <EmploymentSummary application={application} cardProps={cardProps('employment')} />

          <SummaryCard stepId="documents" title={`Documents (${(application.documents || []).length})`}>
            <DocumentGallery documents={application.documents || []} />
          </SummaryCard>

          <SummaryCard stepId="signature" title="Disclosures & signature">
            {disclosures?.items ? (
              <ul className="space-y-1.5">
                {Object.entries(disclosures.items).map(([key, item]) => (
                  <li key={key} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" aria-hidden="true" />
                    <span className="text-gray-900">{legalTitles[key] || key} <span className="text-xs text-gray-500">· accepted {formatDisplayDateTime(item.acceptedAt)}</span></span>
                  </li>
                ))}
                <li className="pt-1 text-xs text-gray-500">Disclosure text version {disclosures.version}</li>
              </ul>
            ) : <p className="text-sm text-gray-500">Not signed yet.</p>}
            {sig && (
              <div className="grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 sm:grid-cols-[220px_minmax(0,1fr)]">
                <div>
                  {application.signatureUrls?.applicant
                    ? <img src={application.signatureUrls.applicant} alt={`Signature of ${sig.typedName}`} className="h-20 w-full rounded border border-gray-200 bg-white object-contain" />
                    : <div className="h-20 rounded border border-dashed border-gray-300" />}
                  <p className="mt-1 text-xs text-gray-500">Applicant: {sig.typedName}</p>
                </div>
                <Rows rows={[
                  ['Signed at', formatDisplayDateTime(sig.signedAt)],
                  ['IP address', sig.ip],
                  ['Device', sig.userAgent, true],
                  ['Signature SHA-256', sig.sha256, true]
                ]} />
              </div>
            )}
            {rsig && (
              <div className="grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 sm:grid-cols-[220px_minmax(0,1fr)]">
                <div>
                  {application.signatureUrls?.reviewer
                    ? <img src={application.signatureUrls.reviewer} alt={`Signature of ${rsig.typedName}`} className="h-20 w-full rounded border border-gray-200 bg-white object-contain" />
                    : <div className="h-20 rounded border border-dashed border-gray-300" />}
                  <p className="mt-1 text-xs text-gray-500">Reviewer: {rsig.typedName}, {rsig.title}</p>
                </div>
                <Rows rows={[
                  ['Signed at', formatDisplayDateTime(rsig.signedAt)],
                  ['IP address', rsig.ip],
                  ['Final PDF SHA-256', application.review?.finalPdf?.sha256, true]
                ]} />
              </div>
            )}
          </SummaryCard>

          <SummaryCard stepId="timeline" title="Activity">
            <ApplicationTimeline timeline={application.timeline} />
          </SummaryCard>
        </div>
      </div>

      {actions.length > 0 && (
        <div className="sticky bottom-0 z-20 -mx-4 mt-6 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {canFlag && flagged.length > 0 && (
              <p className="self-center text-xs text-amber-800 sm:mr-auto">{flagged.length} section{flagged.length > 1 ? 's' : ''} flagged for changes</p>
            )}
            {actions}
          </div>
        </div>
      )}

      <RequestChangesModal open={modal === 'changes'} application={application} initialSections={flagged} onClose={() => setModal(null)} onDone={afterAction} />
      <RejectModal open={modal === 'reject'} application={application} onClose={() => setModal(null)} onDone={afterAction} />
      <ApproveSignModal open={modal === 'approve'} application={application} legal={bundle.legal} currentUser={userData} onClose={() => setModal(null)} onDone={afterAction} />
      <ReviewModal
        open={modal === 'withdraw'}
        title="Withdraw this invite?"
        description={`${name || application.applicant?.email}'s link will stop working and anything they have filled in stays on file.`}
        onClose={() => setModal(null)}
        onConfirm={() => inviteAction('withdraw')}
        confirmLabel="Withdraw invite"
        confirmTone="danger"
        busy={busyAction === 'withdraw'}
      >
        <p className="text-sm text-gray-600">You can send a new invite to the same email later.</p>
      </ReviewModal>
      <HireModal open={modal === 'hire'} application={application} onClose={() => setModal(null)} onDone={afterAction} />
    </div>
  );
};

export default ApplicationDetailPage;
